/* eslint-disable @typescript-eslint/no-unused-vars */
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import React, { useCallback, useMemo, useState } from 'react'

import PendingPlacementChip from 'components/inventory/containers/common/PendingPlacementChip'
import { isSettled, SETTLEMENT_OUTCOMES, settlementLabel } from 'components/inventory/containers/configured-service/deactivation/settlement'
import {
  groupKeyOf,
  identityOf,
  SettlementBulkControls,
  SETTLEMENT_COLUMNS,
  SettlementRow,
  startsGroup,
} from 'components/inventory/containers/configured-service/deactivation/SettlementRowEditors'
import T from 'T'

import type {
  RetrievalResolution,
  SettleableRow,
  SettlementOutcome,
} from 'components/inventory/containers/configured-service/deactivation/settlement'
import type { NamedRow } from 'components/inventory/containers/configured-service/deactivation/SettlementRowEditors'
import type { ContainerSettlement } from 'components/inventory/containers/configured-service/deactivation/useContainerSettlement'

export interface SettlementYard {
  id: string
  name: string
}

// Dropdown shows the type, the WO creation needs the id.
export interface SettlementAction {
  id: string
  actionType: string
}

// An existing WO the rep can attach the container to instead of scheduling a new one.
export interface SettlementWorkOrder {
  workOrderId: string
  workOrderNumber?: string
  serviceDate?: string
  workOrderStatus?: string
  eventActionType?: string
}

type SettlementTab = 'resolve' | 'placements' | 'no-container' | 'customer'

// NamedRow, SettlementRow, SettlementBulkControls, and shared helpers live in SettlementRowEditors.tsx.

const SettlementGroupHeader = ({ label, caption }: { label?: string | null; caption?: string | null }) => (
  <Stack alignItems="baseline" direction="row" spacing={1} sx={{ px: 1, pb: 0.5, pt: 1.5 }}>
    <Typography fontWeight={600} variant="caption">
      {label}
    </Typography>
    {caption && (
      <Typography color="text.secondary" variant="caption">
        {caption}
      </Typography>
    )}
  </Stack>
)

const ConsequenceRows = ({ rows, chip, consequence }: { rows: NamedRow[]; chip: React.ReactNode; consequence: string }) => (
  <Stack divider={<Divider />} spacing={0}>
    {rows.map((row, index) => (
      <React.Fragment key={String(row.id)}>
        {startsGroup(rows, index) && <SettlementGroupHeader caption={row.groupCaption} label={row.groupLabel} />}
        <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={1} sx={{ py: 1 }}>
          <Stack alignItems="center" direction="row" spacing={1}>
            {chip}
            <Typography variant="body2">{identityOf(row)}</Typography>
          </Stack>
          <Typography color="text.secondary" variant="caption">
            {consequence}
          </Typography>
        </Stack>
      </React.Fragment>
    ))}
  </Stack>
)

const ContainerSettlementList = ({
  settlement,
  yards,
  serviceActions = [],
  existingWorkOrders = [],
  disabled = false,
  offeredOutcomes = SETTLEMENT_OUTCOMES,
}: {
  settlement: ContainerSettlement
  yards: SettlementYard[]
  serviceActions?: SettlementAction[]
  existingWorkOrders?: SettlementWorkOrder[]
  disabled?: boolean
  // A whole deactivation asks fewer answers than a single removal does.
  offeredOutcomes?: SettlementOutcome[]
}) => {
  const { real, placements, pendingPlacements, noContainerRows, customerOwned, resolutions, set, setAllUnsettled, settledCount } =
    settlement
  const total = placements.length + real.length + customerOwned.length
  const outstanding = real.length - settledCount

  const [tab, setTab] = useState<SettlementTab>('resolve')

  const handleTabChange = useCallback((_event: React.SyntheticEvent, next: SettlementTab) => setTab(next), [])

  return (
    <Stack spacing={1.5}>
      <Stack alignItems="center" direction="row" spacing={0.5}>
        <Typography fontWeight={600} variant="body2">
          {`${T.CONTAINERS} (${total})`}
        </Typography>
        <Tooltip arrow title={T.SETTLEMENT_HINT}>
          <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        </Tooltip>
      </Stack>

      {(pendingPlacements.length > 0 || noContainerRows.length > 0 || customerOwned.length > 0) && (
        <Tabs value={tab} variant="scrollable" onChange={handleTabChange}>
          <Tab label={`${T.TO_RESOLVE} (${real.length})`} value="resolve" />
          {pendingPlacements.length > 0 && <Tab label={`${T.PENDING_PLACEMENTS} (${pendingPlacements.length})`} value="placements" />}
          {noContainerRows.length > 0 && <Tab label={`${T.NO_CONTAINER} (${noContainerRows.length})`} value="no-container" />}
          {customerOwned.length > 0 && <Tab label={`${T.SETTLEMENT_CUSTOMER_OWNED} (${customerOwned.length})`} value="customer" />}
        </Tabs>
      )}

      {tab === 'placements' && (
        <ConsequenceRows chip={<PendingPlacementChip kind="PENDING" />} consequence={T.WILL_BE_CANCELLED} rows={pendingPlacements} />
      )}
      {tab === 'no-container' && (
        <ConsequenceRows chip={<PendingPlacementChip kind="NONE_EXPECTED" />} consequence={T.WILL_BE_CANCELLED} rows={noContainerRows} />
      )}
      {tab === 'customer' && (
        <ConsequenceRows
          chip={
            <Typography color="text.secondary" variant="caption">
              {T.SETTLEMENT_CUSTOMER_OWNED}
            </Typography>
          }
          consequence={T.STAYS_NOT_YOURS_TO_RETRIEVE}
          rows={customerOwned}
        />
      )}

      {tab === 'resolve' && real.length === 0 && (
        <Typography color="text.secondary" variant="body2">
          {T.NOTHING_TO_RETRIEVE}
        </Typography>
      )}

      {tab === 'resolve' && real.length > 0 && (
        <>
          {real.length > 1 && outstanding > 0 && (
            <SettlementBulkControls
              disabled={disabled}
              existingWorkOrders={existingWorkOrders}
              outstanding={outstanding}
              serviceActions={serviceActions}
              yards={yards}
              onApply={setAllUnsettled}
            />
          )}

          <Stack divider={<Divider />} spacing={0}>
            <Box sx={{ ...SETTLEMENT_COLUMNS, bgcolor: 'background.default', px: 1, py: 0.75 }}>
              <Typography color="text.secondary" variant="caption">
                {T.CONTAINER}
              </Typography>
              <Typography color="text.secondary" variant="caption">
                {T.OUTCOME}
              </Typography>
              <Typography color="text.secondary" variant="caption">
                {T.RETRIEVAL_DATE}
              </Typography>
              <Typography color="text.secondary" variant="caption">
                {T.DESTINATION}
              </Typography>
              <Box />
            </Box>

            {(real as NamedRow[]).map((row, index) => (
              <React.Fragment key={String(row.id)}>
                {startsGroup(real as NamedRow[], index) && <SettlementGroupHeader caption={row.groupCaption} label={row.groupLabel} />}
                <SettlementRow
                  disabled={disabled}
                  existingWorkOrders={existingWorkOrders}
                  offeredOutcomes={offeredOutcomes}
                  resolution={resolutions[String(row.id)]}
                  row={row}
                  serviceActions={serviceActions}
                  yards={yards}
                  onSet={set}
                />
              </React.Fragment>
            ))}
          </Stack>

          <Typography color="text.secondary" sx={{ alignSelf: 'flex-end' }} variant="caption">
            {outstanding === 0 ? T.ALL_SET : `${settledCount} ${T.OF} ${real.length} ${T.PROGRESS_SET_SUFFIX}`}
          </Typography>
        </>
      )}
    </Stack>
  )
}

export default ContainerSettlementList
